export type FaqItem = { q: string; a: string };
export type FaqCategory = { category: string; items: FaqItem[] };

export const faqsByCategory: FaqCategory[] = [
  {
    category: "Apps",
    items: [
      {
        q: "How do I know which app is the right fit?",
        a: "Start with the problem you are trying to solve—forms, events, or a very specific internal workflow. HIPAA-compliant forms are ideal for collecting protected health information, Event Maker is for registrations and ticketing, Rubric is for structured scoring, and Custom Apps are better when none of the above quite fits.",
      },
      {
        q: "Can Apps integrate with my existing tools?",
        a: "Yes. Most Apps can integrate with common CRMs, marketing tools, and back-office systems using webhooks, APIs, or lightweight middleware. During onboarding we’ll review your stack and outline what’s possible and what requires custom work.",
      },
      {
        q: "Can you customize an app just for my business?",
        a: "Yes. For many customers we start with an existing app such as HIPAA-compliant forms or Event Maker and then layer on custom workflows, fields, or integrations so it matches how your team actually works.",
      },
      {
        q: "Do Apps work with both small and growing teams?",
        a: "Apps are designed to work well for very small teams but still support growth—adding new users, roles, and environments as you scale. We can discuss where you are today and where you expect to be in 12–24 months.",
      },
      {
        q: "What is the onboarding process like for a new app?",
        a: "We start with a short discovery call, then map your workflows, configure a test environment, and walk you through a pilot. Once everything looks good, we agree on a go-live plan, training, and basic documentation.",
      },
      {
        q: "How are bugs or feature requests for Apps handled?",
        a: "Bugs and feature requests are tracked in a shared queue. We prioritize issues that affect stability or compliance first, then work through quality-of-life improvements. Larger feature requests are estimated and scheduled with you.",
      },
    ],
  },
  {
    category: "Web Support",
    items: [
      {
        q: "What does Web Support typically cover?",
        a: "Web Support usually includes updates, troubleshooting, performance tuning, security hardening, and help with deployments for WordPress, Next.js, and Joomla sites. We also help with DNS, SSL, email routing, and control panels when they affect your site.",
      },
      {
        q: "How quickly can you respond to issues?",
        a: "Most non-urgent tickets get an initial response within business hours, and priority plans include faster response times plus a clearer escalation path for outages. For critical incidents we work with you to define severity levels and who gets notified.",
      },
      {
        q: "Can you help if someone else built our site?",
        a: "Yes. A lot of our work is supporting or stabilizing sites built by other agencies or freelancers. We start with a brief assessment so we understand the stack, then create a short plan for updates, fixes, and monitoring.",
      },
      {
        q: "Do you handle one-off fixes or only retainers?",
        a: "We support both. Many customers start with a one-off incident or small project and then move to a recurring plan once they see the value of having someone on call who already knows their environment.",
      },
      {
        q: "Can you coordinate with our existing IT or marketing team?",
        a: "Yes. We often work alongside in-house IT, marketing, or external agencies. We clarify who owns which systems, then document a simple communication path so issues do not fall between teams.",
      },
      {
        q: "What information do you need to start supporting our site?",
        a: "Typically we need access to your hosting panel, code repository (if you have one), and any third-party services that affect the site—such as DNS, SSL provider, or email delivery. We document this during onboarding so future work is fast and predictable.",
      },
    ],
  },
  {
    category: "Hosting",
    items: [
      {
        q: "What makes CloudAxe hosting different?",
        a: "CloudAxe hosting focuses on a clean, well-understood stack with monitoring, backups, and restore testing built in. We optimize for small business sites that need reliability and clear communication, not experimental infrastructure.",
      },
      {
        q: "How do backups and restore tests work?",
        a: "Backups are taken on a schedule and stored off-server, and we periodically perform restore tests so we know they work in practice, not just in theory. If you ever need a restore, we already have a tested path to bring your site back online.",
      },
      {
        q: "Can you migrate us from our current host?",
        a: "Yes. We handle the full migration plan—cloning the site, syncing databases and files, handling DNS and SSL, and scheduling the final cutover. The aim is to minimize downtime and avoid surprises during the switch.",
      },
      {
        q: "Do you support staging environments?",
        a: "Yes. For most sites we recommend a staging environment where we can safely test updates, new features, and configuration changes before they reach production.",
      },
      {
        q: "How do you monitor uptime and performance?",
        a: "We use monitoring that checks uptime, key health indicators, and basic performance. When something looks off, we investigate and either handle it directly or coordinate with you, depending on your plan.",
      },
      {
        q: "What happens if our traffic suddenly spikes?",
        a: "If you are on a managed hosting plan, we look at scaling options, caching, and CDN configuration to keep the site stable. We also review what caused the spike so we can right-size the environment going forward.",
      },
    ],
  },
];

/** Flatten categories into Question/Answer entities for FAQPage JSON-LD. */
export function flattenFaqsForJsonLd(): Array<{
  "@type": "Question";
  name: string;
  acceptedAnswer: { "@type": "Answer"; text: string };
}> {
  return faqsByCategory.flatMap((group) =>
    group.items.map((item) => ({
      "@type": "Question" as const,
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer" as const,
        text: item.a,
      },
    }))
  );
}
